import React, { ReactElement, useMemo } from 'react'

import classNames from 'classnames'
import styled from 'styled-components'

import { Point } from '../../api/types'
import iconDirection from '../../assets/images/icon_direction.svg'
import { useBuildingContext } from '../../contexts/buildingContext'
import { useNavigation } from '../../hooks/useNavigation'
import { blue, gray, pink } from '../../styles/colors'

import {
  PanZoomContext,
  PanZoomProvider,
  usePanZoomContext,
} from './PanZoomContext'

const ZoomStep = 0.2

/** Default zoom bounds for the main map; allow zooming out past initial view. */
const DEFAULT_MIN_SCALE = 0.05
const DEFAULT_MAX_SCALE = 2

const PointSize = 30

/**
 * Diameter in image-space so the dot stays roughly the same on-screen size at any zoom.
 * Used when scaleInvariantPointSize is set on MapViewer.
 */
function getScaleAdjustedPointSize(
  scale: number,
  baseScreenDiameter: number,
  min = 12,
  max = 80,
): number {
  return Math.max(min, Math.min(max, baseScreenDiameter / scale))
}
const PointBorder = PointSize / 6
const PointHighlightStart = PointSize + PointBorder * 2
const PointHighlightRadius = PointBorder * 4

const FOVLength = 1500

export interface MapPointMainCircleProps {
  pointSize?: number
  borderSize?: number
  backgroundColors?: string[]
  borderColors?: string[]
}

export const MapPointMainCircle = styled.div.attrs<MapPointMainCircleProps>(
  props => ({
    style: {
      width: props.pointSize ?? PointSize,
      height: props.pointSize ?? PointSize,
      borderWidth: props.borderSize
        ? props.borderSize
        : props.pointSize
          ? props.pointSize / 6
          : PointBorder,
      backgroundImage: props.backgroundColors
        ? `conic-gradient(${props.backgroundColors
            .map(
              (color, i, arr) =>
                `${color} ${(360 / arr.length) * i}deg ${(360 / arr.length) * (i + 1)}deg`,
            )
            .join(', ')})`
        : undefined,
      background:
        props.borderColors && !props.backgroundColors
          ? `linear-gradient(#fff 0 0) padding-box, conic-gradient(${props.borderColors
              .map(
                (color, i, arr) =>
                  `${color} ${(360 / arr.length) * i}deg ${(360 / arr.length) * (i + 1)}deg`,
              )
              .join(', ')}) border-box`
          : undefined,
    },
  }),
)<MapPointMainCircleProps>`
  position: absolute;
  transform: translate(-50%, -50%);
  background-color: white;
  border-radius: 50%;
  border-style: solid;
  border-color: #dbdbdb;
  box-sizing: content-box;
  &.back_pink {
    background-color: ${pink};
    border-color: ${pink};
  }
  &.back_pink_empty {
    background-color: white;
    border-color: ${pink};
  }
  &.back_gray {
    background-color: ${gray};
    border-color: ${gray};
  }
  &.back_gray_empty {
    background-color: white;
    border-color: ${gray};
  }
  &.back_blue {
    background-color: ${blue};
  }
  &.back_blue_empty {
    background-color: white;
    border-color: ${blue};
  }
  &.back_red {
    background-color: #ff5252;
  }
  &.back_red_empty {
    background-color: white;
    border-color: #ff5252;
  }
  &.back_green {
    background-color: #2eb06c;
  }
  &.back_green_empty {
    background-color: white;
    border-color: #2eb06c;
  }
  &.back_yellow {
    background-color: #f5c20c;
  }
  &.back_yellow_empty {
    background-color: white;
    border-color: #f5c20c;
  }
  &.back_orange {
    background-color: #ef7e46;
  }
  &.back_orange_empty {
    background-color: white;
    border-color: #ef7e46;
  }
  &.back_purple {
    background-color: #6b53fd;
  }
  &.back_purple_empty {
    background-color: white;
    border-color: #6b53fd;
  }
  &.scan_health_none {
    background-color: white;
    border-color: #686868;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  }
  &.scan_health_3w {
    background-color: #a8c8e8;
    border-color: #5a8ab8;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  }
  &.scan_health_2w {
    background-color: #4a7ab8;
    border-color: ${blue};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  }
  &.scan_health_1w {
    background-color: ${blue};
    border-color: ${blue};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
  &.border_white {
    border-color: white;
  }
  &.shadow {
    box-shadow: 0 5px 10px rgba(0, 0, 0, 0.15);
  }
`
MapPointMainCircle.displayName = 'MapPointMainCircle'

export const MapPointMainHighlighted = styled.div`
  position: absolute;
  width: ${PointHighlightStart}px;
  height: ${PointHighlightStart}px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  border: ${PointHighlightRadius}px solid rgba(7, 60, 122, 0.1);
  box-sizing: content-box;
`
MapPointMainHighlighted.displayName = 'MapPointMainHighlighted'

const MapPointDirectionArrow = styled.img`
  position: absolute;
  width: ${PointSize + PointBorder * 2}px;
  height: ${PointSize + PointBorder * 2}px;
  transform: translate(-50%, -50%) translateY(-${PointSize / 2}px) rotate(45deg);
  pointer-events: none;
`

const MapPointDirectionCone = styled.div`
  position: absolute;
  width: 0;
  height: 0;
  transform: translate(-50%, -50%) translateY(-${FOVLength / 2}px)
    rotate(180deg);
  border-style: solid;
  border-color: transparent transparent rgba(7, 60, 122, 0.1) transparent;
  pointer-events: none;
`

interface IMapPointContainerProps {
  x: number
  y: number
  isActive: boolean
}

function calcBorder(fov: number) {
  const H = FOVLength
  const fovRad = (fov / 180) * Math.PI
  const halfWidth = H * Math.tan(fovRad / 2)
  return `0 ${halfWidth}px ${H}px ${halfWidth}px`
}

export const MapPointContainer = styled.div.attrs<IMapPointContainerProps>(
  props => ({
    style: {
      left: `${props.x}px`,
      top: `${props.y}px`,
      cursor: props.isActive ? 'pointer' : 'default',
    },
  }),
)<IMapPointContainerProps>`
  position: absolute;
`
MapPointContainer.displayName = 'MapPointContainer'

export const MapPoint = (props: {
  x: number
  y: number
  point: Point
  active?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper selectedDateRange type
  selectedDateRange: any
  highlighted?: boolean
  showDirectionArrow?: boolean
  fov?: number
  rotation?: number
  onClick?: () => void
  /** When set, used for double-click instead of navigating to fullscreen SiteView. */
  onDoubleClick?: () => void
  /** When set (e.g. for safety observation markers), use this class instead of back_blue. */
  severityClass?: string
  /** When set, point size is computed from zoom so it stays roughly constant on screen. */
  scaleInvariantPointSize?: number
  /** Called when the user hovers over this point (or null on leave). */
  onHoverMapPoint?: (point: Point | null) => void
}) => {
  const { state: buildingState } = useBuildingContext()
  const { navigateToPointFullscreen } = useNavigation()
  const panZoomContext = usePanZoomContext()
  const scale = panZoomContext?.scale ?? 0.3
  const effectivePointSize =
    props.scaleInvariantPointSize != null
      ? getScaleAdjustedPointSize(scale, props.scaleInvariantPointSize)
      : undefined

  const isNon360ImageFloor = !!buildingState.floorData?.is_non_360_image_floor

  /*
  a point is active if: it has an image during the selected window
   */
  const isPointActive = useMemo(() => {
    if (isNon360ImageFloor) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- flat image marker
      const p = props.point as any
      const pointImages = Array.isArray(p.images) ? p.images : []
      if (!pointImages.length) {
        return !!p.has_flat_image || !!p.flatImage
      }
      const filtered = pointImages.filter((image: { taken_on: string }) => {
        const pointDate = new Date(image.taken_on)
        return (
          !props.selectedDateRange ||
          (pointDate >= props.selectedDateRange.startDate &&
            pointDate <= props.selectedDateRange.endDate)
        )
      })
      return filtered.length > 0
    }
    if (!buildingState.floorData.images) return false
    const found = buildingState.floorData.images
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper image type
      .filter((image: any) => props.point.point_id == image.sub_point_id)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper image types
      .sort(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper image types
        (a: any, b: any) =>
          new Date(b.taken_on).getTime() - new Date(a.taken_on).getTime(),
      )
    if (!found.length) return false
    let anyFound = false
    for (const point in found) {
      const pointDate = new Date(found[point].taken_on)
      const isInDateRange =
        !props.selectedDateRange ||
        (pointDate >= props.selectedDateRange?.startDate &&
          pointDate <= props.selectedDateRange?.endDate)
      // console.log('Active point: ', isInDateRange, props.point.images?.length);
      if (isInDateRange && props.point.images?.length) anyFound = true
    }
    return anyFound
  }, [
    props.point,
    props.selectedDateRange,
    buildingState.floorData.images,
    isNon360ImageFloor,
  ])

  /** On aerial / non-360 floors every viewpoint is selectable (upload or view). */
  const isPointClickable = isNon360ImageFloor || isPointActive || !!props.active

  const onClick = () => {
    if (props.onClick) {
      props.onClick()
    }
  }

  const onDoubleClickCircle = () => {
    if (props.onDoubleClick) {
      if (isPointActive || props.active) {
        props.onDoubleClick()
      }
    } else if (isPointActive && !isNon360ImageFloor) {
      navigateToPointFullscreen(props.point.point_id)
    }
  }

  const onDoubleClickHighlight = () => {
    if (props.onDoubleClick) {
      props.onDoubleClick()
    } else if (isPointActive && !isNon360ImageFloor) {
      navigateToPointFullscreen(props.point.point_id)
    }
  }

  const circleClass =
    props.severityClass ??
    (isNon360ImageFloor
      ? isPointActive || props.active
        ? 'back_blue'
        : 'back_blue_empty'
      : isPointActive || props.active
        ? 'back_blue'
        : undefined)

  return (
    <MapPointContainer
      className={`map-point mp-${props.point.point_id}`}
      x={props.x}
      y={props.y}
      isActive={isPointClickable}
      onMouseEnter={() => props.onHoverMapPoint?.(props.point)}
      onMouseLeave={() => props.onHoverMapPoint?.(null)}
      onTouchStart={event => {
        event.stopPropagation()
      }}
    >
      {props.showDirectionArrow && (
        <MapPointArrow rotation={props.rotation} fov={props.fov} />
      )}
      <MapPointMainCircle
        className={classNames({
          ...(circleClass ? { [circleClass]: true } : {}),
          border_white: props.highlighted || props.showDirectionArrow,
          shadow: props.showDirectionArrow,
        })}
        pointSize={effectivePointSize}
        title={props.point.markerTitle}
        onClick={isPointClickable ? () => onClick() : () => null}
        onDoubleClick={onDoubleClickCircle}
      />
      {props.highlighted && (
        <MapPointMainHighlighted
          style={
            effectivePointSize != null
              ? {
                  width: effectivePointSize + (effectivePointSize / 6) * 2,
                  height: effectivePointSize + (effectivePointSize / 6) * 2,
                  borderWidth: (effectivePointSize / 6) * 4,
                }
              : undefined
          }
          onClick={() => onClick()}
          onDoubleClick={onDoubleClickHighlight}
        />
      )}
    </MapPointContainer>
  )
}

type PanZoomProps = {
  initialX?: number
  initialY?: number
  minScale?: number
  maxScale?: number
  interactive?: boolean
  preventDoubleTouches?: boolean
}

class PanZoom extends React.Component<PanZoomProps> {
  static contextType = PanZoomContext
  context!: React.ContextType<typeof PanZoomContext>

  private mainRef = React.createRef<HTMLDivElement>()
  private innerRef = React.createRef<HTMLDivElement>()

  private x = 0
  private y = 0
  private isDraggingDown = false
  private isScaling = false
  private dragStartX = 0
  private dragStartY = 0
  private dragStartScale = 0
  private interactive = false
  private lastTouch = 0

  constructor(props: Readonly<PanZoomProps> | PanZoomProps) {
    super(props)

    this.x = props.initialX ?? 0
    this.y = props.initialY ?? 0
    this.interactive = this.props.interactive ?? true

    this.onMouseDown = this.onMouseDown.bind(this)
    this.onMouseUp = this.onMouseUp.bind(this)
    this.onMouseMove = this.onMouseMove.bind(this)
    this.onTouchDown = this.onTouchDown.bind(this)
    this.onTouchUp = this.onTouchUp.bind(this)
    this.onTouchMove = this.onTouchMove.bind(this)
    this.onWheel = this.onWheel.bind(this)

    this.onDragStart = this.onDragStart.bind(this)

    this.onGestureStart = this.onGestureStart.bind(this)
    this.onGestureEnd = this.onGestureEnd.bind(this)
    this.onGestureChange = this.onGestureChange.bind(this)
  }

  public getZoom(): number {
    return this.context.scale
  }

  public setZoom(newZoom: number) {
    const mainDiv = this.mainRef.current!
    const rect = mainDiv.getBoundingClientRect()

    this.setZoomRelativeTo(newZoom, rect.width / 2, rect.height / 2)
  }

  public setPosition(newZoom: number, newX: number, newY: number) {
    this.x = newX
    this.y = newY
    if (this.props.maxScale != null && newZoom > this.props.maxScale)
      newZoom = this.props.maxScale
    if (this.props.minScale != null && newZoom < this.props.minScale)
      newZoom = this.props.minScale
    this.updatePosition(newZoom)
  }

  public setZoomRelativeTo(newZoom: number, x: number, y: number) {
    if (this.props.maxScale != null && newZoom > this.props.maxScale)
      newZoom = this.props.maxScale
    if (this.props.minScale != null && newZoom < this.props.minScale)
      newZoom = this.props.minScale

    const currentScale = this.context.scale
    const scaleIsFinite =
      typeof currentScale === 'number' && Number.isFinite(currentScale)
    if (!Number.isFinite(newZoom)) {
      newZoom = this.props.minScale ?? DEFAULT_MIN_SCALE
    }
    const scaleMult = scaleIsFinite ? newZoom / currentScale : 1

    this.x = x - (x - this.x) * scaleMult
    this.y = y - (y - this.y) * scaleMult

    this.updatePosition(newZoom)
  }

  private updatePosition(newZoom?: number) {
    let scale = newZoom ?? this.context.scale
    if (typeof scale !== 'number' || !Number.isFinite(scale)) {
      scale = this.props.minScale ?? DEFAULT_MIN_SCALE
    }
    const divOverlay2 = this.innerRef.current!
    divOverlay2.style.transform = `translate(${this.x}px, ${this.y}px) translate(-50%,-50%) scale(${scale}) translate(50%,50%)`

    this.context.setScale(scale)
  }

  public componentDidMount() {
    const mainDiv = this.mainRef.current!

    if (this.interactive) {
      document.addEventListener('mouseup', this.onMouseUp, true)
      document.addEventListener('mousemove', this.onMouseMove, true)
      document.addEventListener('touchend', this.onTouchUp, true)
      document.addEventListener('touchmove', this.onTouchMove, true)
      mainDiv.addEventListener('wheel', this.onWheel, {
        passive: false,
        capture: true,
      })
      mainDiv.addEventListener('dragstart', this.onDragStart, false)
      mainDiv.addEventListener('gesturestart', this.onGestureStart, false)
      mainDiv.addEventListener('gestureend', this.onGestureEnd, false)
      mainDiv.addEventListener('gesturechange', this.onGestureChange, false)
    }
    this.updatePosition()
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper prevProps and prevState types
  public componentDidUpdate(prevProps: any, _prevState: any) {
    if (
      prevProps.initialX !== this.props.initialX ||
      prevProps.initialY !== this.props.initialY
    ) {
      this.x = this.props.initialX ?? 0
      this.y = this.props.initialY ?? 0
      this.setZoomRelativeTo(0.3, this.x, this.y)
    }
  }

  public componentWillUnmount() {
    const mainDiv = this.mainRef.current!

    if (this.interactive) {
      document.removeEventListener('mouseup', this.onMouseUp, false)
      document.removeEventListener('mousemove', this.onMouseMove, false)
      document.removeEventListener('touchend', this.onTouchUp, false)
      document.removeEventListener('touchmove', this.onTouchMove, false)
      mainDiv.removeEventListener('wheel', this.onWheel, {
        passive: false,
        capture: true,
      } as EventListenerOptions)
      mainDiv.removeEventListener('dragstart', this.onDragStart, false)
      mainDiv.removeEventListener('gesturestart', this.onGestureStart, false)
      mainDiv.removeEventListener('gestureend', this.onGestureEnd, false)
      mainDiv.removeEventListener('gesturechange', this.onGestureChange, false)
    }
  }

  private onMouseDown(event: React.MouseEvent<HTMLDivElement>) {
    this.isDraggingDown = true
    this.dragStartX = event.clientX
    this.dragStartY = event.clientY
  }

  private onTouchDown(event: React.TouchEvent) {
    const currentTouch = new Date().getTime()
    const isDoubleTouch: boolean = currentTouch - this.lastTouch <= 200
    const eligibleToProceed = !isDoubleTouch || !this.props.preventDoubleTouches

    if (eligibleToProceed && event.touches.length === 1) {
      this.isDraggingDown = true
      this.dragStartX = event.touches[0].clientX
      this.dragStartY = event.touches[0].clientY
    }

    this.lastTouch = currentTouch
  }

  private onMouseUp() {
    this.isDraggingDown = false
  }

  private onTouchUp() {
    this.isDraggingDown = false
  }

  private onMouseMove(event: MouseEvent) {
    event.preventDefault()
    if (this.isDraggingDown) {
      this.x += event.clientX - this.dragStartX
      this.y += event.clientY - this.dragStartY
      this.dragStartX = event.clientX
      this.dragStartY = event.clientY
      this.updatePosition()
    }
  }

  private onTouchMove(event: TouchEvent) {
    event.preventDefault()
    if (this.isDraggingDown) {
      if (event.touches.length === 1) {
        this.x += event.touches[0].clientX - this.dragStartX
        this.y += event.touches[0].clientY - this.dragStartY
        this.dragStartX = event.touches[0].clientX
        this.dragStartY = event.touches[0].clientY
        this.updatePosition()
      }
    }
  }

  private onWheel(e: WheelEvent) {
    const mainDiv = this.mainRef.current!
    if (!mainDiv) return

    const rect = mainDiv.getBoundingClientRect()
    const x = e.clientX
    const y = e.clientY
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom)
      return

    e.preventDefault()
    e.stopPropagation()

    // deltaY > 0 = scroll down = zoom out (standard convention)
    const dir = e.deltaY > 0 ? 1 : -1

    // Prefer wheelDeltaY (legacy); fall back to deltaY (standard). Use magnitude only.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper event type
    const rawDelta = (e as any).wheelDeltaY ?? e.deltaY
    const delta =
      typeof rawDelta === 'number' && Number.isFinite(rawDelta)
        ? Math.abs(rawDelta)
        : 120
    const zoomStep = 1 + ZoomStep * Math.max(0, Math.min(1.5, delta / 120))

    const mouseX = x - rect.left
    const mouseY = y - rect.top
    const scaleMult = dir === 1 ? 1 / zoomStep : zoomStep

    const currentScale = this.context.scale
    const newScale = currentScale * scaleMult
    this.setZoomRelativeTo(newScale, mouseX, mouseY)
  }

  private onDragStart(e: Event) {
    e.preventDefault()
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper event type
  private onGestureStart(e: any) {
    e.preventDefault()
    this.dragStartScale = e.scale
    this.isScaling = true
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper event type
  private onGestureEnd(e: any) {
    e.preventDefault()
    this.isScaling = false
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper event type
  private onGestureChange(e: any) {
    e.preventDefault()
    if (this.isScaling) {
      const diff = e.scale / this.dragStartScale
      this.dragStartScale = e.scale

      const mainDiv = this.mainRef.current!

      if (e.target) {
        const rect = mainDiv.getBoundingClientRect()

        const mouseX = e.clientX - rect.x
        const mouseY = e.clientY - rect.y

        this.setZoomRelativeTo(this.context.scale * diff, mouseX, mouseY)
      }
    }
  }

  render() {
    return (
      <div
        ref={this.mainRef}
        style={{ width: '100%', height: '100%', overflow: 'hidden' }}
        onMouseDown={this.onMouseDown}
        onTouchStart={this.onTouchDown}
      >
        <div
          style={{ position: 'relative', display: 'inline-block' }}
          ref={this.innerRef}
        >
          {this.props.children}
        </div>
      </div>
    )
  }
}

interface IMapViewerProps {
  children?: React.ReactNode
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper image type
  image: any
  setLoadedImg: (value: string) => void
  imageLoaded: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper selectedDateRange type
  selectedDateRange: any
  points: Point[]
  highlightedPointId?: string | null
  showDirectionArrow?: boolean
  angleOffset?: number
  rotation?: number
  fov?: number
  onDoubleClickMap?: (e: React.MouseEvent) => void
  onMouseMoveMap?: (e: React.MouseEvent) => void
  preventDoubleTouches?: boolean
  showPoints?: boolean
  onClickMapPoint?: (point: Point) => void
  /** When set, double-click invokes this instead of fullscreen SiteView (e.g. Progress Tracking). */
  onDoubleClickMapPoint?: (point: Point) => void
  /** When true, all points render as active (blue). Use for observation markers. */
  allPointsActive?: boolean
  /**
   * When set, point diameter is adjusted by zoom so dots stay roughly this many pixels on screen.
   * Use for consistent visible size regardless of floor plan resolution or zoom (e.g. safety gallery).
   */
  scaleInvariantPointSize?: number
  /** Called when the user hovers over a point (or null on leave). Use e.g. to prefetch observation. */
  onHoverMapPoint?: (point: Point | null) => void
  /**
   * When set, the map opens zoomed on this floor-plan coordinate instead of
   * fitting the whole floor (e.g. review workspace minimap).
   */
  focusPoint?: { x: number; y: number } | null
  /** Change when the overlay size changes so the focused point stays centered. */
  focusLayoutKey?: string | number
}

interface IMapViewerState {
  initialPositionSet: boolean
}

export class MapViewer extends React.Component<
  IMapViewerProps,
  IMapViewerState
> {
  constructor(props: Readonly<IMapViewerProps> | IMapViewerProps) {
    super(props)

    this.state = {
      initialPositionSet: false,
    }

    this.onImageLoad = this.onImageLoad.bind(this)
  }

  private mapRef = React.createRef<PanZoom>()
  private imgRef = React.createRef<HTMLImageElement>()
  /** Scale used when the floor plan was first fit to the viewport. */
  private fitScale = 0.3

  public static defaultProps = {
    showPoints: true,
  }

  public componentDidMount() {
    this.syncLoadedImageState()
  }

  public componentDidUpdate(prevProps: IMapViewerProps) {
    if (prevProps.image !== this.props.image) {
      this.setState({ initialPositionSet: false })
    } else if (
      this.state.initialPositionSet &&
      (prevProps.focusPoint?.x !== this.props.focusPoint?.x ||
        prevProps.focusPoint?.y !== this.props.focusPoint?.y ||
        prevProps.focusLayoutKey !== this.props.focusLayoutKey)
    ) {
      this.applyView(false)
    }
    this.syncLoadedImageState()
  }

  private syncLoadedImageState() {
    const img = this.imgRef.current
    if (!img || !img.complete || img.naturalWidth === 0) {
      return
    }
    if (this.props.imageLoaded) {
      return
    }
    this.handleImageLoaded(img)
  }

  private applyView(updateFitScale: boolean) {
    const map = this.mapRef.current
    const img = this.imgRef.current
    if (!map || !img || !img.clientWidth || !img.clientHeight) {
      return false
    }

    const screenContainer = img.parentElement?.parentElement
    if (!screenContainer) {
      return false
    }

    const { width, height } = screenContainer.getBoundingClientRect()
    if (width <= 0 || height <= 0) {
      return false
    }

    const fitScale = Math.min(
      DEFAULT_MAX_SCALE,
      Math.max(DEFAULT_MIN_SCALE, height / img.clientHeight - 0.005),
    )
    if (updateFitScale) {
      this.fitScale = fitScale
    }

    const focus = this.props.focusPoint
    const focusX = Number(focus?.x)
    const focusY = Number(focus?.y)
    if (focus && Number.isFinite(focusX) && Number.isFinite(focusY)) {
      // Six zoom-out steps from the tight neighborhood (~1.2^6).
      const tightScale = Math.min(
        DEFAULT_MAX_SCALE,
        Math.max(0.3, Math.min(width, height) / 400),
      )
      const localScale = Math.max(
        DEFAULT_MIN_SCALE,
        tightScale / (1 + ZoomStep) ** 6,
      )
      map.setPosition(
        localScale,
        width / 2 - focusX * localScale,
        height / 2 - focusY * localScale,
      )
      return true
    }

    map.setPosition(
      fitScale,
      width / 2 - (img.clientWidth * fitScale) / 2,
      height / 2 - (img.clientHeight * fitScale) / 2,
    )
    return true
  }

  private handleImageLoaded(_target: HTMLImageElement) {
    if (!this.state.initialPositionSet) {
      if (this.applyView(true)) {
        this.setState({
          initialPositionSet: true,
        })
      } else {
        window.requestAnimationFrame(() => {
          if (this.applyView(true)) {
            this.setState({
              initialPositionSet: true,
            })
          }
        })
      }
    }

    this.props.setLoadedImg(this.props.image)
  }

  public recenterOnFocus() {
    this.applyView(false)
  }

  public zoomIn() {
    const map = this.mapRef.current
    if (map) map.setZoom(map.getZoom() * (1 + ZoomStep))
  }

  public zoomOut() {
    const map = this.mapRef.current
    if (map) map.setZoom(map.getZoom() / (1 + ZoomStep))
  }

  /** Current CSS transform scale of the floor plan. */
  public getZoom(): number {
    return this.mapRef.current?.getZoom() ?? this.fitScale
  }

  /**
   * Zoom relative to the initial “fit-to-viewport” scale (1 = default fit,
   * >1 = zoomed in). Used to frame the 3D camera to match the 2D view.
   */
  public getZoomRelativeToFit(): number {
    const fit = this.fitScale > 0 ? this.fitScale : DEFAULT_MIN_SCALE
    return this.getZoom() / fit
  }

  private onImageLoad(e: React.SyntheticEvent<HTMLImageElement, Event>) {
    this.handleImageLoaded(e.target as HTMLImageElement)
  }

  render() {
    return (
      <PanZoomProvider initialScale={0.3}>
        <PanZoom
          ref={this.mapRef}
          minScale={DEFAULT_MIN_SCALE}
          maxScale={DEFAULT_MAX_SCALE}
          preventDoubleTouches={this.props.preventDoubleTouches}
        >
          <img
            ref={this.imgRef}
            src={this.props.image}
            alt=""
            id="floor-plan"
            onLoad={this.onImageLoad}
            className={`smooth-image image-${this.props.imageLoaded ? 'visible' : 'hidden'}`}
            key={this.props.image}
            onDoubleClick={this.props.onDoubleClickMap}
            onMouseMove={this.props.onMouseMoveMap}
          />
          {this.props.imageLoaded && (
            <>
              {this.props.children}
              {this.props.showPoints &&
                this.props.points.map(point => (
                  <MapPoint
                    key={point.point_id}
                    selectedDateRange={this.props.selectedDateRange}
                    point={point}
                    active={this.props.allPointsActive}
                    highlighted={
                      String(point.point_id) ===
                      String(this.props.highlightedPointId)
                    }
                    showDirectionArrow={
                      String(point.point_id) ===
                        String(this.props.highlightedPointId) &&
                      this.props.showDirectionArrow
                    }
                    rotation={this.props.rotation}
                    fov={this.props.fov}
                    x={point.x}
                    y={point.y}
                    severityClass={point.severityClass}
                    scaleInvariantPointSize={this.props.scaleInvariantPointSize}
                    onHoverMapPoint={this.props.onHoverMapPoint}
                    onClick={() => {
                      if (this.props.onClickMapPoint) {
                        this.props.onClickMapPoint(point)
                      }
                    }}
                    onDoubleClick={
                      this.props.onDoubleClickMapPoint
                        ? () => this.props.onDoubleClickMapPoint!(point)
                        : undefined
                    }
                  />
                ))}
            </>
          )}
        </PanZoom>
      </PanZoomProvider>
    )
  }
}

interface IMinimapViewerProps {
  image: string
  point: Point
  fov: number
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper selectedDateRange type
  selectedDateRange: any
  rotation: number
  tourPoints?: Point[]
  onClickTourPoint?: (point: Point) => void
  boundingBox?: string
  /** Observation position on floor plan (e.g. from labels[0].floor_plan_position). Optional severityClass for marker color (fallback: yellow). */
  observationPoint?: { x: number; y: number; severityClass?: string }
}

export class MinimapViewer extends React.Component<IMinimapViewerProps> {
  private mapRef = React.createRef<PanZoom>()
  private memoizedPoints: ReactElement[] = []

  private renderTourPoints(): ReactElement[] {
    const tourPoints: Point[] = this.props.tourPoints || []
    // Current capture already has the FOV marker (often at true pose). Skip its
    // planned pin so we never show a double-dot next to the view indicator.
    const currentId = this.props.point?.point_id
    return tourPoints
      .filter(point => String(point.point_id) !== String(currentId))
      .map(point => (
        <MapPoint
          key={point.point_id}
          selectedDateRange={this.props.selectedDateRange}
          point={point}
          highlighted={false}
          x={point.x}
          y={point.y}
          onClick={() => {
            if (this.props.onClickTourPoint) {
              this.props.onClickTourPoint(point)
            }
          }}
        />
      ))
  }

  public zoomIn() {
    const map = this.mapRef.current
    if (map) map.setZoom(map.getZoom() * (ZoomStep + 1))
  }

  public zoomOut() {
    const map = this.mapRef.current
    if (map) map.setZoom(map.getZoom() / (ZoomStep + 1))
  }

  componentDidMount() {
    this.memoizedPoints = this.renderTourPoints()
  }

  componentDidUpdate(prevProps: IMinimapViewerProps) {
    if (
      prevProps.tourPoints !== this.props.tourPoints ||
      prevProps.point?.point_id !== this.props.point?.point_id
    ) {
      this.memoizedPoints = this.renderTourPoints()
    }
  }

  render() {
    if (!this.props.point.x) return <></>

    const boundingBoxComponents = this.props.boundingBox
      ?.split(',')
      .map(Number) || [0, 0, 0, 0]
    const hasBoundingBox =
      boundingBoxComponents.length >= 4 &&
      boundingBoxComponents.every(n => !Number.isNaN(n))
    const observationPoint =
      this.props.observationPoint ??
      (hasBoundingBox
        ? {
            x: boundingBoxComponents[0] + boundingBoxComponents[2] / 2,
            y: boundingBoxComponents[1] + boundingBoxComponents[3] / 2,
          }
        : null)

    return (
      <PanZoomProvider initialScale={0.3}>
        <PanZoom
          ref={this.mapRef}
          initialX={-this.props.point.x * 0.3 + 200}
          initialY={-this.props.point.y * 0.3 + 110}
          minScale={0.1}
          maxScale={1}
          interactive={true}
        >
          <img src={this.props.image} alt="" />
          <MapPoint
            active={true}
            selectedDateRange={this.props.selectedDateRange}
            point={this.props.point}
            highlighted={false}
            showDirectionArrow={true}
            fov={this.props.fov}
            rotation={this.props.rotation}
            x={this.props.point.x}
            y={this.props.point.y}
          />
          {this.memoizedPoints}
          {observationPoint && (
            <MapPointContainer
              x={observationPoint.x}
              y={observationPoint.y}
              isActive={false}
            >
              <MapPointMainCircle
                className={`${observationPoint.severityClass ?? 'back_yellow'} border_white`}
                style={{ width: 14, height: 14, borderWidth: 2 }}
              />
            </MapPointContainer>
          )}
        </PanZoom>
      </PanZoomProvider>
    )
  }
}

interface MapPointArrowProps {
  rotation?: number
  fov?: number
}

export const MapPointArrow = ({ rotation, fov }: MapPointArrowProps) => {
  return (
    <div style={{ transform: `rotate(${rotation}deg)`, pointerEvents: 'none' }}>
      <MapPointDirectionArrow src={iconDirection} alt="" />
      {fov && fov !== -1 && (
        <MapPointDirectionCone
          style={{
            borderWidth: calcBorder(fov),
          }}
        />
      )}
    </div>
  )
}
